export interface TemplateModel {
    templateId: number;
    templateName: string;
    shortCode?: string;
    description?: string;
    typeId?: number;
    typeName?: string;
    businessUnitId?: number;
    selectedRoleIds?: number[];
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
    updatedBy?: number;
    createdBy?: number;
    sections?: TemplateSectionModel[];
    // Add more fields as needed
}

export interface ImportTemplateModel {
    templateId: number;
    subtemplateIds: number[];
    updatedBy?: number;
    parentSectionId?: number | null; // For importing as subsections
}

export interface TemplateTypeModel {
    templateTypeId: number;
    typeName: string;
    description?: string;
    sortOrder?: number;
}

export interface BusinessUnitModel {
    id: number;
    name: string;
    displayName?: string;
    description?: string;
}

export interface RoleModel {
    roleId: number;
    roleName?: string;
}

export interface TemplateSectionModel {
    sectionId?: number;
    templateId?: number;
    sectionName?: string;
    description?: string;
    sortOrder?: number;
    parentSectionId?: number | null;
    updatedAt?: string;
    updatedBy?: number;
    createdAt?: string;
    createdBy?: number;
    lineItemList?: LineItemModel[];
    subsections?: TemplateSectionModel[]; // For hierarchical display
    isExpanded?: boolean; // For collapse/expand UI state
}

export interface LineItemModel {
    lineItemId: number;
    sectionId?: number;
    name?: string;
    controlTypeId?: number;
    integrationMappingId?: number | null;
    selectedIntegrationType?: string | null;
    integrationMappings?: LineItemIntegrationMappingModel[];
    isRequired: boolean;
    sortOrder?: number;
    updatedBy?: number;
    updatedAt?: Date;
    defaultText?: string;
    additionalInfo?: string | null;
    validationConfig?: string | null;
    lineItemOptionValueList?: LineItemOptionValueModel[];
    lineItemTableColumnList?: LineItemTableColumnModel[];
}

export interface ReferenceImageModel {
    id: string;
    fileName?: string | null;
    filePath: string;
    thumbnailPath?: string | null;
}

export interface LineItemAdditionalInfoModel {
    referenceImages?: ReferenceImageModel[];
    [key: string]: unknown;
}

export function parseLineItemAdditionalInfo(additionalInfo?: string | null): LineItemAdditionalInfoModel {
    if (!additionalInfo) {
        return {};
    }

    try {
        const parsed = JSON.parse(additionalInfo);
        return typeof parsed === 'object' && parsed !== null ? parsed as LineItemAdditionalInfoModel : {};
    } catch {
        return {};
    }
}

export interface ControlTypeModel {
    controlTypeId: number;
    controlTypeName: string;
}

export interface TableDataTypeModel {
    dataTypeId: number;
    dataTypeName: string;
}

export interface LineItemOptionValueModel {
    optionValueId?: number;
    valueText: string;
    lineItemId?: number;
    isDeleted?: boolean;
}

export interface LineItemTableColumnModel {
    columnId?: number;
    dataTypeId?: number;
    columnName?: string;
    lineItemId?: number;
    integrationMappingId?: number | null;
    selectedIntegrationType?: string | null;
    integrationMappings?: LineItemIntegrationMappingModel[];
    isRequired?: boolean;
    isDeleted?: boolean;
    columnOptionValueList?: LineItemTableColumnOptionValueModel[];
}

export interface LineItemIntegrationMappingModel {
    integrationType?: string;
    integrationMappingId?: number | null;
}

export interface LineItemTableColumnOptionValueModel {
    columnOptionValueId?: number;
    columnId?: number;
    valueText?: string;
    sortOrder?: number;
    isActive?: boolean;
    createdBy?: number;
    createdAt?: string;
    updatedBy?: number;
    updatedAt?: string;
    isDeleted?: boolean;
}

// Control type enum for easy reference
export enum ControlType {
    TextBox = 1,
    TextArea = 2,
    CheckBox = 3,
    Select = 4,
    Table = 5,
    DatePicker = 6,
    Attachment = 7,
    Picture = 8,
    Signature = 9,
    DigitalSignature = 10,
    QRScanner = 11,
    Weather = 12,
    RichText = 13,
    LabelText = 14,
    Tasks = 15,
    Integer = 16,
    Decimal = 17,
    Users = 18,
    DateTimePicker = 19,
    Judgement = 20,
    Skids = 21,
    ImpactedArea = 22

}

// Data type enum for table columns
export enum DataType {
    ShortText = 1,
    LongText = 2,
    Integer = 3,
    Decimal = 4,
    Date = 5,
    CheckBox = 6,
    Select = 7,
    Tasks = 8,
    Users = 9,
    DateTimePicker = 10,
    Skids = 11
}

// File control value interface
export interface FileControlValue {
    newFiles: File[];           // Files selected but not uploaded yet
    existingFileIds: number[];  // IDs of already uploaded files
}

// For storing table row data - using columnId as key
export interface TableRowData {
    [key: string]: any; // col_{columnId}: value
}

// Digital signature value interface
export interface DigitalSignatureValue {
    signatures: DigitalSignatureItem[];
}

export interface DigitalSignatureItem {
    signerId?: number;        // User ID (if available)
    signerName: string;       // Display name of signer
    signerEmail?: string;     // Email (if available)
    signedAt: string;         // Local timestamp with offset for new signatures; legacy entries may be UTC ISO
    signedAtTimeZone?: string; // IANA timezone id, e.g. Asia/Kolkata
    signedAtTimeZoneLabel?: string; // Short timezone label, e.g. IST
    deviceOwner: string;      // Name of device owner (logged-in user)
    signatureText: string;    // The text to display in cursive
}

// Combined section child item for unified ordering
export type SectionChildItem =
    | { type: 'lineItem'; data: LineItemModel; sortOrder: number }
    | { type: 'subsection'; data: TemplateSectionModel; sortOrder: number };

// Project Template Skid Status interface
export interface ProjectTemplateSkidStatusModel {
    id?: number;
    blockScheduleId: number;
    projectTemplateId: number;
    status?: number;
    isOverride?: boolean;
    assigneedBy?: number;
    assigneeName?: string;
    date?: string | Date;
    duedate?: string | Date;
    createdBy?: number;
    createdAt?: string;
    updatedBy?: number;
    updatedAt?: string;
    createdByName?: string;
    updatedByName?: string;
}

export interface CloneTemplateRequest {
    sourceTemplateId: number;
    templateName: string;
    updatedBy?: number;
}
